import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Switch, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { useManageEntityAccount } from '../hooks/useManageEntityAccount'
import {
  ManageEntityAccountForm,
  ManageEntityAccountSchema,
} from '../schemas/ManageEntityAccountSchema'
import { useSelectedApplicationStore } from '../stores/useSelectedApplicationStore'

export function DrawerManageEntityAccount() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-manage-entity-account')
  const { application } = useSelectedApplicationStore()
  const { handleManageEntityAccount, isManagingEntityAccount } =
    useManageEntityAccount()

  const hookForm = useForm<ManageEntityAccountForm>({
    resolver: zodResolver(ManageEntityAccountSchema()),
    defaultValues: {
      entityAccount: false,
    },
  })

  const handleSubmit = async (data: ManageEntityAccountForm) => {
    if (!application?.id) return

    await handleManageEntityAccount(
      application.id,
      data.entityAccount,
      null,
    ).then(() => {
      close()
    })
  }

  const handleCancelManageEntityAccount = () => {
    hookForm.reset()
    close()
  }

  useEffect(() => {
    if (application) {
      const hasEntityAccount = application.requiresEntityAccount || false

      hookForm.reset({
        entityAccount: hasEntityAccount,
      })
    }
  }, [application, hookForm])

  return (
    <Drawer.Root id="drawer-manage-entity-account">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:applications.drawer_manage_entity_account.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:applications.drawer_manage_entity_account.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="entityAccount">
                {t(
                  'sso:applications.drawer_manage_entity_account.fields.requires_entity_account.label',
                )}
              </Switch.Label>
              <Switch.Trigger name="entityAccount" />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root
            type="button"
            variant="secondary"
            onClick={handleCancelManageEntityAccount}
            disabled={isManagingEntityAccount}
          >
            {t(
              'sso:applications.drawer_manage_entity_account.footer.close_button.label',
            )}
          </Button.Root>

          <Button.Root
            type="button"
            onClick={hookForm.handleSubmit(handleSubmit)}
            isLoading={isManagingEntityAccount}
            disabled={isManagingEntityAccount}
          >
            {t(
              'sso:applications.drawer_manage_entity_account.footer.submit_button.label',
            )}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
